import React, { type PropsWithChildren, useState, useRef } from 'react';

import cn from 'classnames';
import { Link } from 'gatsby';
import { useDialogStore, type DialogStore } from '@ariakit/react';
import { Link as A, Paragraph as P, Heading as H } from '@periodica/ui-kit';
import { CaretDown as ArrowDownIcon, CaretUp as ArrowUpIcon } from '@periodica/ui-kit/icons';
import * as Modal from '@periodica/ui-kit/modal';
import * as Tooltip from '@periodica/ui-kit/tooltip';
import gtm from '@periodica/analytics/gtm';
import type { StrapiDropdownItem, StrapiFooterNavItem } from '@customTypes/strapiData';
import AppQr from '@images/footer/AppQr.svg';
import AppQrBig from '@images/footer/AppQrBig.svg';
import AppleIcon from '@images/footer/AppleIcon.svg';
import SkolkovoResident from '@images/footer/SkolkovoResident.png';
import { APP_STORE_LINK } from '@consts/appsConst';
import { isUrlExternal } from '@utils/isUrlExternal';
import { Vk, Tg, Dzen } from './icons';

import styles from './styles.module.scss';

const socials = [
  {
    url: 'https://vk.com/periodica_press',
    icon: Vk,
    key: 'vk',
  },
  {
    url: 'https://t.me/periodicapress',
    icon: Tg,
    key: 'telegram',
  },
  {
    url: 'https://zen.yandex.ru/periodicapress',
    icon: Dzen,
    key: 'zen',
  },
];

type LinkBlockArgs = {
  block: StrapiFooterNavItem;
};

type SocialBlockArgs = {
  Icon: () => JSX.Element;
  url: string;
};

export function Footer({ blocks }: { blocks: StrapiFooterNavItem[] }) {
  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.mainPart}>
            <AppInfoBlock />

            <DesktopLinkBlocks blocks={blocks} />
            <MobileBlocks blocks={blocks} />
          </div>
          <CompanyInfoBlock />
          <P size="small" color="secondary">
            Мы используем{' '}
            <A href="/privacy/#rec748128838" target="_blank">
              файлы cookie
            </A>
            , чтобы сделать использование сайта более удобным для&nbsp;вас. Все&nbsp;права защищены.
            Использование материалов возможно только с&nbsp;обязательным указанием ссылки
            на&nbsp;первоисточник.
          </P>
        </div>
      </div>
    </footer>
  );
}

function AppInfoBlock() {
  const dialogStore = useDialogStore();

  const showQRModal = () => {
    gtm.logEvent({ name: 'event_site_footer_show_qr' });
    dialogStore.show();
  };

  const handleClickAppStoreLink = () => {
    gtm.logEvent({ name: 'event_site_click_appstore_link' });
  };

  return (
    <div className={styles.appInfo}>
      <div className={styles.externalLinksPart}>
        <div className={styles.socials}>
          <div className={styles.socialLinks}>
            {socials.map((social: any) => (
              <SocialBlock key={social.key} Icon={social.icon} url={social.url} />
            ))}
          </div>
          <div className={styles.socialTextBlock}>
            <P weight="emphasized" color="secondary">
              Готовы начать?
            </P>
            <P>
              <A href="/products/photobooks/">Соберите свою книгу</A>
            </P>
          </div>
        </div>
      </div>
      <div className={styles.appsBlock}>
        <div className={styles.appsText}>
          <BlockTitle>В приложении — удобней</BlockTitle>
          <P color="secondary">
            Скачайте приложение в&nbsp;App&nbsp;Store и&nbsp;собирайте фотокниги прямо
            в&nbsp;своем&nbsp;телефоне
          </P>
        </div>

        <div className={styles.appLinksContainer}>
          <Tooltip.Provider placement="bottom">
            <Tooltip.Anchor
              render={
                <button className={styles.appQrButton} type="button" onClick={showQRModal}>
                  <img src={AppQr} alt="Кнопка с QR-код со ссылкой, которая ведет в AppStore" />
                </button>
              }
            />
            <Tooltip.Tooltip gutter={8}>Показать QR-код</Tooltip.Tooltip>
          </Tooltip.Provider>
          <AppQrModal dialogStore={dialogStore} />
          <Tooltip.Provider placement="bottom">
            <Tooltip.Anchor
              render={
                <a
                  className={styles.appStoreLink}
                  href={APP_STORE_LINK}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={handleClickAppStoreLink}
                >
                  <div className={styles.appStoreLinkContent}>
                    <img src={AppleIcon} alt="Логотип компании Эппл" />
                  </div>
                </a>
              }
            />
            <Tooltip.Tooltip gutter={8}>Открыть App Store</Tooltip.Tooltip>
          </Tooltip.Provider>
        </div>
      </div>
    </div>
  );
}

function CompanyInfoBlock() {
  return (
    <div className={styles.companyInfoBlock}>
      <div className={styles.adress}>
        <P color="secondary">
          ООО «Периодика Пресс» <br /> ИНН 7743125098 <br /> ОГРН&nbsp;1157746985285
        </P>
      </div>
      <div className={styles.adress}>
        <P color="secondary">
          Юридический адрес: 125315, г.&nbsp;Москва, <br /> Ленинградский проспект, д.&nbsp;74А
          <br />
          E-mail <A href="mailto:help@periodica.press">help@periodica.press</A>
        </P>
      </div>

      <div className={styles.adressMobileBlock}>
        <P size="small" color="secondary">
          ООО «Периодика Пресс», ИНН 7743125098, ОГРН&nbsp;1157746985285
        </P>
        <P size="small" color="secondary">
          Юридический адрес: 125315, г.&nbsp;Москва, Ленинградский проспект, д.&nbsp;74А,
          E-mail&nbsp;<A href="mailto:help@periodica.press">help@periodica.press</A>
        </P>
      </div>
      <div className={styles.noneBlock} />
      <div className={styles.partners}>
        <div className={styles.partnerBlock}>
          <img src={SkolkovoResident} alt="" />
        </div>
      </div>
    </div>
  );
}

function BlockTitle({ children }: PropsWithChildren<{}>) {
  return (
    <P size="medium" weight="emphasized">
      {children}
    </P>
  );
}

function DesktopLinkBlocks({ blocks }: { blocks: StrapiFooterNavItem[] }) {
  return (
    <div className={styles.desktopBlocks}>
      {blocks.map((block) => (
        <div className={styles.desktopBlock} key={`block_${block.rank}`}>
          <BlockTitle>{block.title}</BlockTitle>
          <div className={styles.linksContainer}>
            {block?.items?.map((item) => <LinkItem key={`link_${item.title}`} item={item} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function MobileBlocks({ blocks }: { blocks: StrapiFooterNavItem[] }) {
  const [activeBlockRank, setActiveBlockRank] = useState<number>();

  const toggleLinkBlock = (rank: number) => {
    setActiveBlockRank(activeBlockRank === rank ? undefined : rank);
  };

  return (
    <div className={styles.mobileBlocks}>
      {blocks.map((block) => (
        <MobileLinkBlock
          block={block}
          isOpen={activeBlockRank === block.rank}
          onClick={() => {
            toggleLinkBlock(block.rank);
          }}
          key={`mobileBlock_${block.rank}`}
        />
      ))}
    </div>
  );
}

function MobileLinkBlock({
  block,
  isOpen,
  onClick,
}: LinkBlockArgs & { isOpen: boolean; onClick: () => void }) {
  const contentHeight = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.mobileBlock}>
      <button type="button" className={styles.toggle} onClick={onClick}>
        <BlockTitle>{block.title}</BlockTitle>
        {isOpen ? <ArrowUpIcon size={20} /> : <ArrowDownIcon size={20} />}
      </button>
      <div
        className={cn([styles.linksWrapper, isOpen && styles.open])}
        ref={contentHeight}
        style={isOpen ? { height: contentHeight?.current?.scrollHeight } : { height: '0px' }}
      >
        <div className={styles.linksContainer}>
          {block?.items?.map((item) => <LinkItem key={`mobileLink_${item.title}`} item={item} />)}
        </div>
      </div>
    </div>
  );
}

function LinkItem({ item }: { item: StrapiDropdownItem }) {
  const icon = item.icon ? <img src={item.icon?.localFile?.publicURL} alt="Иконка" /> : null;
  return item.externalUrl || isUrlExternal(item.url) ? (
    <A
      key={`link_${item.title}`}
      variant="primary"
      href={item.url}
      target="_blank"
      rel="noopener nofollow noreferrer"
      className={styles.linkWithIcon}
    >
      {item.title}
      {icon}
    </A>
  ) : (
    <A key={`link_${item.title}`} variant="primary" asChild className={styles.linkWithIcon}>
      <Link to={item.url}>
        {item.title}
        {icon}
      </Link>
    </A>
  );
}

function SocialBlock({ Icon, url }: SocialBlockArgs) {
  return (
    <a
      href={url}
      className={styles.socialContainer}
      target="_blank"
      rel="noopener nofollow noreferrer"
    >
      <Icon />
    </a>
  );
}

function AppQrModal({ dialogStore }: { dialogStore: DialogStore }) {
  return (
    <Modal.Wrapper size="small" store={dialogStore}>
      <Modal.Content>
        <div className={styles.qrModal}>
          <H variant="h2">В приложении — удобней</H>
          <img src={AppQrBig} alt="QR-код со ссылкой, которая ведет в AppStore" />
          <P>
            Загружайте фото с&nbsp;телефона, собирайте уникальные книги сами или с&nbsp;помощью
            нейросети, которая учитывает ваши&nbsp;пожелания. Сохраните важные моменты&nbsp;легко!
          </P>
        </div>
      </Modal.Content>
    </Modal.Wrapper>
  );
}
